import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { fetch } from '../../../utils/api-wrapper';

export const useTransactionSelectionAndSubmission = ({ mappedTransactions, fetchStatement, statement, statement_id, t }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [someRowsAreReadyForSubmit, setSomeRowsAreReadyForSubmit] = useState(false);
  const [allTransactionsReady, setAllTransactionsReady] = useState(false);
  const [isSubmittingSelected, setIsSubmittingSelected] = useState(false);
  const [someTransactionsAreAlreadySubmitted, setSomeTransactionsAreAlreadySubmitted] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setAllTransactionsReady(
      mappedTransactions.length > 0 && mappedTransactions.filter((tx) => tx.readyForSubmit).length === mappedTransactions.length
    );
  }, [mappedTransactions]);

  const handleRowSelect = (rows) => {
    setSelectedRows(rows);
    setSomeRowsAreReadyForSubmit(rows.some((r) => r.readyForSubmit));
    setSomeTransactionsAreAlreadySubmitted(rows.some((r) => r.raw_transaction.submitted));
  };

  const setSelectedRowsToReadyForSubmit = (setToReady = true) => {
    if (!selectedRows || selectedRows.length === 0) return;
    const rawTransactions = selectedRows.map((r) => r.raw_transaction).filter((tx) => tx?.transaction_id);
    const unmatched = [];

    rawTransactions.forEach((tx) => {
      if (setToReady && !tx.json) {
        unmatched.push(tx);
        return;
      }
      fetch(
        '/plaid/transaction',
        'PUT',
        {
          transaction_id: tx.transaction_id,
          updated_fields: { readyForSubmit: setToReady },
        },
        () => {
          if (isMountedRef.current) {
            fetchStatement?.();
          }
        },
        (error) => {
          console.error('Failed to update transaction', tx.transaction_id, error);
        }
      );
    });

    if (unmatched.length > 0) {
      const list = unmatched.map((tx) => tx.transaction_name || tx.name).join('\n• ');
      toast.error(`${t.read('cc_transactions.cannot_mark_unmatched') || 'Cannot mark unmatched transactions as ready for submit:'}\n• ${list}`);
    }

    setSelectedRows([]);
    setSomeRowsAreReadyForSubmit(false);
  };

  const decorateStatement = (statement) => {
    if (!statement) return statement;
    const total = statement.expenses.reduce((sum, expense) => sum + expense.Total, 0);
    const tax1 = statement.expenses.reduce((sum, expense) => sum + (expense.Tax1 || 0), 0);
    const tax2 = statement.expenses.reduce((sum, expense) => sum + (expense.Tax2 || 0), 0);
    const taxTotal = tax1 + tax2;
    const tip = statement.expenses.reduce((sum, expense) => sum + (expense.Tip || 0), 0);
    return { ...statement, total, tax1, tax2, taxTotal, tip };
  };

  const submitSelectedTransactions = async () => {
    if (!selectedRows || selectedRows.length === 0) return;
    const readyRows =  selectedRows.filter((r) => r.raw_transaction?.transaction_id);

    setIsSubmittingSelected(true);
    let successCount = 0;
    let failCount = 0;
    let transactionIdList = [];
    for (const row of readyRows) {
        if (row.raw_transaction.submitted) {failCount++; continue;}
        if (!row.json) {failCount++; continue;}
        if (!row.checksum) {failCount++; continue;}
        const txId = row.raw_transaction.transaction_id;
        transactionIdList.push(txId);
      }

    if (transactionIdList.length === 0) {
      toast.error(t.read('cc_transactions.submitted_selected_fail'));
      setIsSubmittingSelected(false);
      cleanupSelectedRows();
      return;
    }
    
    await fetch(
          `/c_card/transaction/submit/multiple/${statement_id}`,
          'PUT',
          {"transaction_ids": transactionIdList},
          (res) => {
              if (isMountedRef.current) {
                successCount = transactionIdList.length;
              }
          },
          (res) => {
              if (!isMountedRef.current) return;
              const data = res.response.data 
              if (data.message === 'already_submitted') {
                  toast.error(t.read('cc_transactions.error_already_submitted'));
              }
              else {
                  toast.error(t.read('cc_transactions.submitted_selected_fail'));
                  
              }
          }
      )

      
    if (!isMountedRef.current) return;
    
    if (successCount > 0 && failCount === 0) {
      toast.success(t.read('cc_transactions.submitted_selected_success'));
    }
    if (successCount > 0 && failCount > 0) {
      toast.success(t.read('cc_transactions.submitted_selected_partial_success'));
    }
    if (failCount > 0) {
      toast.error(t.read('cc_transactions.submitted_selected_fail'));
    }

    cleanupSelectedRows();
    if (isMountedRef.current) {
      fetchStatement?.();
    }
  };

  const cleanupSelectedRows = () => {
    if (!isMountedRef.current) return;
    setSelectedRows([]);
    setSomeRowsAreReadyForSubmit(false);
    setIsSubmittingSelected(false);
  }

  return {
    selectedRows,
    someRowsAreReadyForSubmit,
    allTransactionsReady,
    handleRowSelect,
    setSelectedRowsToReadyForSubmit,
    submitSelectedTransactions,
    isSubmittingSelected,
    someTransactionsAreAlreadySubmitted
  };
};
